import * as z from "zod";
import {
  type ActorType,
  actorTypes,
  type DelegatedAccessKind,
  delegatedAccessKinds,
  type FieldAccess,
  type SuiteApp,
} from "./enums";

// --- Results and errors, the same in HTTP and RPC. The error types come from the schemas that also describe them
// in the gateways' OpenAPI documents (ADR-046). ---

/**
 * A business validation failure: which field (of which row, in a list) breaks which rule, e.g. `required`. `value`
 * names what the rule is about when the field alone does not, e.g. the reference value without a mapping.
 */
export const validationIssue = z.object({
  field: z.string(),
  rule: z.string(),
  row: z.number().int().optional(),
  value: z.string().max(200).optional(),
});
export type ValidationIssue = z.output<typeof validationIssue>;

/**
 * Structured details of a domain error, e.g. `{ from, to }` or `{ issues }`: never sensitive values.
 * Non-recursive: Cloudflare RPC types drop `unknown` members and cannot handle recursive types.
 */
const errorDetails = z.record(
  z.string(),
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z.union([z.string(), z.number()])),
    z.array(validationIssue),
  ]),
);
export type ErrorDetails = z.output<typeof errorDetails>;

/**
 * The details of the 409 of an optimistic lock (ADR-282): when the row was written last, and by whom when the reader
 * may see that person. Absent when the domain could not read them.
 */
export const versionConflictDetails = z.object({ changedAt: z.iso.datetime(), changedBy: z.string().optional() });
export type VersionConflictDetails = z.output<typeof versionConflictDetails>;

const apiError = z.object({
  status: z.number().int(),
  code: z.string(),
  /** Safe for any client: never key values, queries or parameters (H6). */
  message: z.string(),
  details: errorDetails.optional(),
  /** Technical detail (Postgres message and detail): internal Actors only. */
  debug: z.string().optional(),
});
export type ApiError = z.output<typeof apiError>;

/** Body of every 4xx and 5xx response of the gateways. */
export const apiErrorBody = z.object({ error: apiError }).meta({ id: "ApiErrorBody" });

/** An OpenAPI response with a JSON body: the routes of the gateways and of the TIL contract (ADR-046, ADR-049). */
export function jsonResponse<S extends z.ZodType>(schema: S, description: string) {
  return { content: { "application/json": { schema } }, description };
}

/**
 * Error codes every API can answer with (50-api/README), besides the domain ones of each module
 * (`CatalogErrorCode`…): the apps give each one a text (ADR-046). `pg_<SQLSTATE>` is left out: never translated.
 */
export type CommonErrorCode =
  | "validation_error"
  | "not_found"
  | "forbidden"
  /** A gate with four eyes (ADR-201): who asked for the decision does not approve it. */
  | "requester_cannot_approve"
  | "field_read_only"
  | "conflict"
  | "invalid_transition"
  | "unique_violation"
  | "foreign_key_violation"
  | "check_violation"
  | "not_null_violation"
  | "invalid_input"
  | "query_canceled"
  | "http_error"
  | "internal_error";

/** `ms`: operation duration in the domain (`execute`), exposed by the gateway in `Server-Timing`. */
export type Result<T> = { ok: true; data: T; ms?: number } | { ok: false; error: ApiError; ms?: number };

/**
 * One `Server-Timing` entry of a gateway response: `op` the domain operation (ADR-026), `auth` the check of the session
 * (session, credential version and Actor), so the browser tells the network from the gateway and from the domain.
 */
export const serverTiming = (ms: number, metric: "op" | "auth" = "op") => `${metric};dur=${ms}`;

/** The operation duration read back from a `Server-Timing` header, or null if it has none. */
export function operationMs(header: string | null): number | null {
  const dur = /(?:^|,)\s*op;dur=([\d.]+)/.exec(header ?? "")?.[1];
  return dur === undefined ? null : Number(dur);
}

// --- Lists (50-api/README §4) ---

/** One page of a list: `hasMore` instead of a total count, which would cost a query. */
export interface Page<T> {
  page: number;
  pageSize: number;
  hasMore: boolean;
  items: T[];
}

export { pagination } from "./pagination";

/** Response schema of a `Page<T>`: the gateway typecheck compares it with the `Page` the domain returns. */
export function pageOf<T extends z.ZodType>(item: T) {
  return z.object({ page: z.number().int(), pageSize: z.number().int(), hasMore: z.boolean(), items: z.array(item) });
}

/**
 * How many items a list has with the same filters, and the earliest of the dates the list says they wait from: what
 * a queue shows without reading the list (ADR-086).
 */
export const listCountDto = z
  .object({ count: z.number().int(), oldest: z.iso.datetime().nullable() })
  .meta({ id: "ListCount" });
export type ListCount = z.output<typeof listCountDto>;

/** More than any list has queues: the home asks one call per list, never an unbounded batch (ADR-269). */
export const MAX_COUNT_FILTERS = 20;

/** Input of the count of one list under several filters at once (ADR-269): the filters of its count, in order. */
export const countByFiltersInput = <S extends z.ZodType>(filter: S) =>
  z.object({ filters: z.array(filter).min(1).max(MAX_COUNT_FILTERS) });

/** The answer of one filter of a count by filters, in the order asked: its count, or the code that refused it alone. */
export type FilterCount = { counted: ListCount } | { refused: string };

/**
 * The rows of a list in at least one of several sets of its filters (ADR-302): the union of the queues of a place, which
 * the app composes from `workQueues`, the domain never knowing the queues. A set is the filters of the list without
 * `q`: no queue searches text, and a text search scans the table under RLS (trap 20), once per set. The other filters
 * of the input still apply to every row. In a query string a JSON array; via RPC, the array the gateway parsed.
 */
export const anyOfFilters = <Shape extends z.ZodRawShape>(filters: Shape) => {
  const { q: _q, ...set } = filters;
  const sets = z.array(z.object(set)).min(1).max(MAX_COUNT_FILTERS);
  return z
    .union([
      sets,
      z
        .string()
        .max(4000)
        .transform((text, ctx) => {
          try {
            return JSON.parse(text) as unknown;
          } catch {
            ctx.addIssue({ code: "custom", message: "Not a JSON array of filters" });
            return z.NEVER;
          }
        }),
    ])
    .pipe(sets);
};

/** A filter and the sets of its `anyOf` (ADR-302): what a check of a filter, or the threshold it needs, looks at. */
export const filterSets = <S extends object>(filter: S & { anyOf?: readonly S[] | undefined }): readonly S[] => [
  filter,
  ...(filter.anyOf ?? []),
];

/**
 * The sets of `anyOf` each row of a page is in, by row id, as indexes in the order asked (ADR-302): what tells why a row
 * is to do. Only with `anyOf`.
 */
export type Matched = Record<string, number[]>;
const matchedDto = z.record(z.string(), z.array(z.number().int().min(0)));

/** A page of a list that takes `anyOf`, with the sets each row is in. */
export interface MatchingPage<T> extends Page<T> {
  matched?: Matched | undefined;
}

/** The input of a list that takes `anyOf` as a query string carries it: the sets as JSON text, which the gateway parses. */
export type WithAnyOfText<I extends { anyOf?: unknown }> = Omit<I, "anyOf"> & { anyOf?: string | undefined };

/** Response schema of a `MatchingPage<T>`. */
export const matchingPageOf = <T extends z.ZodType>(item: T) => pageOf(item).extend({ matched: matchedDto.optional() });

/** A page of a list read by cursor (keyset): `nextCursor` is null on the last page. */
export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

/** `cursor` (opaque, absent on the first page) and `limit` of a list read by cursor; the limits are the list's own. */
export function cursorPagination({ defaultLimit, maxLimit }: { defaultLimit: number; maxLimit: number }) {
  return {
    cursor: z.string().min(1).max(500).optional(),
    limit: z.coerce.number().int().min(1).max(maxLimit).default(defaultLimit),
  };
}

/** Every item of a list read by cursor, page after page: what an export writes, what a job reads. */
export async function allPages<T>(page: (cursor: string | undefined) => Promise<CursorPage<T>>): Promise<T[]> {
  const items: T[] = [];
  for (let cursor: string | null | undefined; cursor !== null; ) {
    const read = await page(cursor);
    items.push(...read.items);
    cursor = read.nextCursor;
  }
  return items;
}

/** Response schema of a `CursorPage<T>`. */
export function cursorPageOf<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

// --- Values ---

/** Reference data travels by code (ADR-046): a season by its code (`FW27`), a category or a showroom by its name. */
export const code = z.string().trim().min(1).max(100);

/** A value a form offers: its code, which travels (ADR-046), and its name. */
export const codeName = z.object({ code: z.string(), name: z.string() });

/**
 * The version of the words a person read when making a choice about their emails (ADR-314 §4): a short slug kept with
 * the proof of the choice (`crm.consent_event.text_version`, varchar(40)), never free text.
 */
export const consentTextVersion = z.string().regex(/^[a-z0-9.-]{1,40}$/);

/** The language of a user, with or without a region (`en`, `it`, `en-GB`): what `Intl` takes as a locale. */
export const languageTag = z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/);

/** Decimal as text, the way Postgres `numeric` travels (money, rates, percentages): never a float. */
export const decimal = z.string().regex(/^-?\d+(\.\d+)?$/);

/** A numeric column as the DB returns it (`10.0000`) and as a client sends it (`10`) are the same value. */
/** An EAN or a GTIN as it travels: 8 to 14 digits, without the spaces a paste brings. */
export const ean = z
  .string()
  .trim()
  .regex(/^\d{8,14}$/, "8 to 14 digits");

export const sameDecimal = (a: string | null, b: string | null): boolean =>
  a === null || b === null ? a === b : Number(a) === Number(b);

// Who is the master of the commercial master data (ADR-288).
export * from "./crm-master";
// Calendar days (`YYYY-MM-DD`, UTC), for the deadlines the domains count and the emails state.
export * from "./days";
// The values of the Postgres enums that travel in the contracts, without zod (ADR-271): @nexxt/db builds its enums from
// them, through `@nexxt/contracts/enums`.
export * from "./enums";
// Search across the records of a domain (ADR-117): the input, the hit and the answer every domain shares.
export * from "./search";
// What blocks a season, the data that will stop a sync towards Business Central or Joor (ADR-327).
export * from "./season-blockers";
// The deadlines of a season, by the domain that owns them (ADR-325): what each domain answers and worklist merges.
export * from "./season-deadlines";
// The scorecard of brands and suppliers by season (ADR-326): the measures each domain answers and worklist merges.
export * from "./season-scorecard";
// The clock of a time zone: of a showroom (booking), of Tomorrow for the runs of the allocation (sales).
export * from "./zoned-time";

/** The version of a row the client read: a write with an older one answers 409 (optimistic lock, ADR-046). */
export const version = z.number().int().min(1);

/** The same from a query string, for a `DELETE`: written out so the OpenAPI document says integer and minimum. */
export const versionQuery = z.object({ version: z.coerce.number().int().min(1) });

/**
 * Boolean of a query string, `"true" | "false" | "1" | "0"` (never `z.coerce.boolean`, which turns "false" into
 * true), or already a boolean: the gateway passes the parsed value on to the RPC method, which validates it again.
 */
export const flag = z.union([
  z.boolean(),
  z.enum(["true", "false", "1", "0"]).transform((v) => v === "true" || v === "1"),
]);

/**
 * The token of a personal link: 256 bits in base64url, 43 characters (`generateToken` of `@nexxt/core`). It
 * travels in the fragment of the link, which no server receives, and only its SHA-256 is stored (ADR-147, ADR-175).
 */
export const linkToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);

/** Bytes in lowercase hex: how digests, signatures and ids are written. */
export function toHex(bytes: ArrayBuffer | Uint8Array): string {
  return Array.from(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
}

/** A SHA-256 in lowercase hex, as `sha256Hex` writes it: a file's digest, the handle of a session. */
export const SHA256_HEX = /^[0-9a-f]{64}$/;
export const sha256HexText = z.string().regex(SHA256_HEX);

/** SHA-256 of a text in hex: how a token or a session id is stored, and a key that must not hold its text. */
export async function sha256Hex(text: string): Promise<string> {
  return toHex(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)));
}

// --- Identifiers ---

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** uuid in its text form (any version): how ids travel between the layers and how the RLS context accepts them. */
export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

/** Id generated by the client for a creation (ADR-046, 50-api/README §7): a retry sends the same one. */
export const uuidV7 = z.uuid({ version: "v7" });

/**
 * A new uuid v7 (RFC 9562): 48 bits of milliseconds, version, 74 random bits. The apps make the id of a creation with
 * it when the form opens, so a retry sends the same one.
 */
export function newUuidV7(now: number = Date.now()): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  for (let i = 0; i < 6; i++) bytes[i] = Math.floor(now / 2 ** (8 * (5 - i))) & 0xff;
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = toHex(bytes);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Milliseconds since the epoch written in a uuid v7 (its first 48 bits). */
export function uuidV7Time(id: string): number {
  return Number.parseInt(id.replace(/-/g, "").slice(0, 12), 16);
}

// --- Who makes the request (ADR-014) ---

/** One change of an audit entry, values as text (a date, a number, a code). */
export const auditChangeDto = z.object({ field: z.string(), from: z.string().nullable(), to: z.string().nullable() });

/** One entry of the history of an entity (FR-M1-07), the same for every domain that serves one. */
export const auditEntryDto = z
  .object({
    id: z.number().int(),
    occurredAt: z.iso.datetime(),
    entity: z.string(),
    entityId: z.uuid(),
    /** What names the entity among its siblings, when it still exists: the style code of a line. */
    label: z.string().nullable(),
    action: z.string(),
    from: z.string().nullable(),
    to: z.string().nullable(),
    reason: z.string().nullable(),
    changes: z.array(auditChangeDto),
    actorType: z.enum(actorTypes),
    /** First and last name, null for a process or a user without a name. */
    actorName: z.string().nullable(),
    /**
     * The delegated access the user made the change with (ADR-262): a personal token or a connection of an assistant
     * (Claude), by the name it had then; absent for a change made in an app.
     */
    via: z
      .object({
        kind: z.enum(delegatedAccessKinds),
        name: z.string(),
        /**
         * The access only prepared the change, and the user confirmed it in Backstage (ADR-265): "prepared by Claude,
         * confirmed in Backstage". Absent when the access made it.
         */
        confirmedInApp: z.boolean().optional(),
      })
      .optional(),
  })
  .meta({ id: "AuditEntry" });

/**
 * Field rules: entity (`catalog.line_price`) → DTO field in snake_case (`wholesale_price`) → access.
 * Only restricted fields are listed: a missing field is `write`.
 */
export type FieldRules = Record<string, Record<string, FieldAccess>>;

/** Built by the gateway from the session (P2.1/P2.2) and passed via RPC to the domains, for permissions, RLS and audit. */
export interface Actor {
  type: ActorType;
  /** Authenticated user; null for system and integration. */
  userId: string | null;
  /**
   * User at the origin of the action, set whenever there is one: `userId` for a user, whoever started
   * the process for system and integration. It is the only "who" field that audit and outbox read.
   */
  causedBy: string | null;
  orgId: string | null;
  /** Scope organizations of the roles (`party.user_role.scope_org_id`), for RLS. */
  orgIds: string[];
  isInternal: boolean;
  roles: string[];
  permissions: string[];
  fieldRules: FieldRules;
  /**
   * An external user with a role that requires a second factor (`party.role.requires_mfa`, ADR-151): gateway-atelier
   * lets a session without one reach only the routes that add one. Absent for everyone else.
   */
  mfaRequired?: true;
  /**
   * The apps the roles of a user open (`party.role.audience`, ADR-157), sorted; every gateway admits only the users of
   * its app. Set, possibly empty, on the Actor of a user built by identity; absent for every other Actor, which never
   * enters an app: absent counts as none.
   */
  apps?: SuiteApp[];
  /**
   * A call a user made through a delegated access (ADR-262), a personal token or the connection of an assistant: the
   * permissions are already narrowed to the access, and the audit names it. Absent for a call from an app.
   */
  delegation?: ActorDelegation;
}

/** The delegated access of a call: what the audit and the history say of it. */
export interface ActorDelegation {
  accessId: string;
  kind: DelegatedAccessKind;
  /** The name the holder gave the token, or the name of the client of the connection (`Claude`). */
  name: string;
  /**
   * The holder's name as identity has it (first and last name), for the presence of the live rooms alone (ADR-300
   * §7): "Claude (for Ada Lovelace)" on the block it wrote. Absent when the holder has no name, or the Actor was built
   * elsewhere.
   */
  holderName?: string;
}

/**
 * An action a delegated access prepared through MCP and its holder confirmed in Backstage (ADR-265, ADR-039 point 3):
 * the operation runs with the holder's own Actor, and the audit names the access that prepared it.
 */
export interface PreparedActionOrigin extends ActorDelegation {
  actionId: string;
}

/** First argument of every domain RPC method: who is calling, and with which request. */
export interface Caller {
  actor: Actor;
  requestId: string;
  /**
   * Set by gateway-backstage alone, on the call that confirms a prepared action, after identity let its holder claim
   * it (ADR-265). Absent on every other call.
   */
  preparedAction?: PreparedActionOrigin;
}

// --- Browser ↔ gateway ---

/**
 * Header the apps send on every request that changes data (ADR-013, point 5; ADR-037): a cross-site form or image
 * cannot send it, and a cross-site script can only after a CORS preflight the gateways never allow.
 */
export const CSRF_HEADER = "x-nexxt-csrf";

/**
 * Session cookie of the gateways (ADR-013, ADR-032), without its prefix: always set with `__Host-` (Secure, Path=/,
 * no Domain), so no subdomain can set or overwrite it.
 */
export const SESSION_COOKIE = "nexxt_session";
